import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft,
  Download,
  Filter,
  Search,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  Eye,
  User
} from 'lucide-react';
import { fetchAdminPaymentHistory } from '../../../services/PaymentService';

interface Payment {
  _id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  description: string;
  paymentMethod: string;
  transactionId: string;
  createdAt: string;
  processedAt?: string;
  project: {
    _id: string;
    title: string;
  };
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const AdminPaymentHistory: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const paymentsData = await fetchAdminPaymentHistory();
      setPayments(paymentsData);
      setError('');
    } catch (error) {
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'completed': return 'text-green-300 bg-green-900/30';
        case 'pending': return 'text-yellow-300 bg-yellow-900/30';
        case 'failed': return 'text-red-300 bg-red-900/30';
        default: return 'text-gray-300 bg-gray-800';
      }
    } else {
      switch (status) {
        case 'completed': return 'text-green-600 bg-green-100';
        case 'pending': return 'text-yellow-600 bg-yellow-100';
        case 'failed': return 'text-red-600 bg-red-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment?.project?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment?.transactionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${payment?.client?.firstName} ${payment?.client?.lastName}`.toLowerCase().includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const paymentDate = new Date(payment.createdAt);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case 'last-30-days':
          matchesDate = diffDays <= 30;
          break;
        case 'last-90-days':
          matchesDate = diffDays <= 90;
          break;
        case 'this-year':
          matchesDate = paymentDate.getFullYear() === now.getFullYear();
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className={`${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } p-6 rounded-lg shadow-sm border animate-pulse`}>
              <div
                className={`h-4 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-3/4 mb-4`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-full mb-2`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-2/3`}
              ></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/admin/payments"
            className={`p-2 ${
              theme === 'dark' ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1
              className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              Payment History
            </h1>
            <p
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}
            >
              View and manage all payment transactions
            </p>
          </div>
        </div>
        <button
          className={`flex items-center px-4 py-2 ${
            theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
          } text-white rounded-lg transition-colors duration-200`}
        >
          <Download className="w-4 h-4 mr-2" />
          Export History
        </button>
      </div>

      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center">
            <XCircle className={`w-5 h-5 ${
              theme === 'dark' ? 'text-red-400' : 'text-red-500'
            } mr-2`} />
            <span className={`${
              theme === 'dark' ? 'text-red-300' : 'text-red-700'
            } text-sm`}>{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } p-6 rounded-lg shadow-sm border`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              } w-4 h-4`} />
              <input
                type="text"
                placeholder="Search payments or clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Time</option>
              <option value="last-30-days">Last 30 Days</option>
              <option value="last-90-days">Last 90 Days</option>
              <option value="this-year">This Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments List */}
      <div className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-lg shadow-sm border`}>
        <div className={`p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Payment Transactions
          </h2>
        </div>

        <div className="p-6">
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className={`w-16 h-16 ${
                theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
              } mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-2`}>No payments found</h3>
              <p className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mb-6`}>
                {payments.length === 0 
                  ? "No payment transactions have been recorded yet."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPayments.map((payment) => (
                <div
                  key={payment._id}
                  className={`border ${
                    theme === 'dark'
                      ? 'border-gray-700 hover:bg-gray-750'
                      : 'border-gray-200 hover:bg-gray-50'
                  } rounded-lg p-6 hover:shadow-md transition-colors duration-200`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div
                          className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            payment.status
                          )}`}
                        >
                          {getStatusIcon(payment.status)}
                          <span className="ml-2 capitalize">{payment.status}</span>
                        </div>
                        <span
                          className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          Project: {payment?.project?.title}
                        </span>
                      </div>

                      <h3
                        className={`font-semibold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        } mb-2`}
                      >
                        {payment.description}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Client:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {payment.client.firstName} {payment.client.lastName}
                          </p>
                        </div>

                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Payment Method:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {payment.paymentMethod}
                          </p>
                        </div>

                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Transaction ID:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {payment.transactionId || 'Pending'}
                          </p>
                        </div>

                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Date:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {payment.processedAt 
                              ? new Date(payment.processedAt).toLocaleDateString() 
                              : new Date(payment.createdAt).toLocaleDateString()
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 ml-6">
                      <div className="text-right">
                        <div
                          className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {formatCurrency(payment.amount, payment.currency)}
                        </div>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {getRelativeTime(payment.createdAt)}
                        </p>
                      </div>
                      <Link
                        to={`/dashboard/admin/payments/${payment._id}`}
                        className={`p-2 ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        } rounded-lg transition-colors duration-200`}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentHistory;